import { setupSlider } from "@omnia/shared/components/slider"
import { defineComponent, IOmniaShell, param } from "../../../omnia"
import { FertiliserAppUnit } from "../../../types"
import { colorToCss } from "@omnia/shared/color/color"
import { getNutrientBaseColor } from "@omnia/shared/nutrition/colors"


export class RateLimitsCtrl {

    static create(): ng.IDirective {

        const def = defineComponent(RateLimitsCtrl, "app/nutrition/plan/edit/rate-limits/limits.html", {
            min: "=",
            max: "=",
            cutOff: "=",
            calculatedMax: "=",
            units: "=",
            nutrient: "=",
            disabled: "=",
        })


        def.link = (scope, element: ng.IAugmentedJQuery, attrs, ctrl: RateLimitsCtrl) => ctrl.link(element)

        return def


    };

    private _cleanup: Function

    public min: number
    public max: number
    public cutOff: number

    public units: FertiliserAppUnit
    public nutrient: string

    public calculatedMax: number

    public disabled: boolean

    private timeout: ng.IPromise<any>

    static $inject = ["$scope", "OmniaShell"];

    constructor(private $scope: ng.IScope, private shell: IOmniaShell) {
    }

    private link(element: ng.IAugmentedJQuery) {

        const outer = element[0].querySelector(".s")

        const cleanup = [
            setupSlider(outer.querySelector(".slider.min"), outer, p => this.setMinFromSlider(p)),
            setupSlider(outer.querySelector(".slider.max"), outer, p => this.setMaxFromSlider(p)),
            setupSlider(outer.querySelector(".cut-off"), outer, p => this.setCutOffFromSlider(p))
        ]

        this._cleanup = () => cleanup.forEach(f => f())

    }

    $onDestroy() {
        this._cleanup && this._cleanup()
    }

    get actualMax() {
        return Math.max(this.calculatedMax || 0, this.max || 0)
    }

    getCutOffPct() {
        return this.getLimitPct(this.cutOff, 0)
    }
    getCutOffStyle() {
        return this.getMarkerStyle(this.getCutOffPct())
    }

    getMinPct() {
        return this.getLimitPct(this.min, 0)
    }
    getMinStyle() {
        return this.getMarkerStyle(this.getMinPct())
    }

    getMaxStyle() {
        return this.getMarkerStyle(this.getMaxPct())
    }
    getMaxPct() {
        return this.getLimitPct(this.max, 1)
    }

    getMarkerStyle(pct: number) {
        return {
            left: pct.toString() + "%"
        }
    }

    getLimitPct(value: number, defaultValue: number) {

        value = (!value) || (this.calculatedMax <= 0)
            ? defaultValue
            : Math.min(this.calculatedMax, value) / this.calculatedMax

        const pct = Math.round(10000 * value) / 100

        return pct

    }

    getBackgroundStyle() {

        const min = this.getMinPct()
        const cutOff = this.getCutOffPct()
        const max = this.getMaxPct()

        const color = getNutrientBaseColor(this.nutrient)
        const cssColor = colorToCss(color) || "green"

        const style = {
            background: (cutOff > min)
                ? `linear-gradient(to right, #aaa, #aaa ${cutOff}%, ${cssColor} ${cutOff}%, ${cssColor} ${max}%, #fff ${max}%)`
                : `linear-gradient(to right, #aaa, #aaa ${cutOff}%, #fff ${cutOff}%, #fff ${min}%, ${cssColor} ${min}%, ${cssColor} ${max}%, #fff ${max}%)`
        }

        return style

    }

    private setMinFromSlider(pct: number) {

        if (this.disabled) return

        const value = this.applyPct(pct)
        const max = this.max || this.calculatedMax

        if ((value >= 0) && (value < max)) {
            this.applyOnTimeout(() => this.min = value)
        }

    }

    private setMaxFromSlider(pct: number) {

        if (this.disabled) return

        const value = this.applyPct(pct)

        const min = Math.max(0, this.min || 0, this.cutOff || 0)

        if (value > min) {
            this.applyOnTimeout(() => this.max = value)
        }

    }

    private setCutOffFromSlider(pct: number) {

        if (this.disabled) return

        const value = this.applyPct(pct)

        const max = this.max || this.calculatedMax

        if ((value >= 0) && (value < max)) {
            this.applyOnTimeout(() => this.cutOff = value)
        }

    }

    private applyOnTimeout(a: () => void) {
        this.timeout = (this.timeout || this.shell.timeout(200, true)).then(() => a())
    }

    private applyPct(pct: number) {

        let r = 1

        switch (this.units) {
            case FertiliserAppUnit.T:
            case FertiliserAppUnit.M3:
                r = 10;
                break;
        }

        return Math.round(pct * this.calculatedMax * r) / r

    }

    getUnitsText() {
        const p = param.FertiliserAppUnit().find(i => i.value == this.units)
        return p ? p.text + "/ha" : ""
    }

}
